import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { updateCircuitRoute, getCircuitRoute } from "@/Api/circuit_routes";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

const schema = z.object({
  title: z.string({ message: "*requerido" }).min(1, "*requerido"),
  starts: z.any({ message: "*requerido" }),
});

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({ updateFront, planId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        setIsLoading(true);
        try {
          const { data: responseApi } = await getCircuitRoute(planId);

          form.setValue("title", responseApi?.response?.title || "");
          const { day, month, year } = responseApi?.response?.starts || {};
          form.setValue(
            "starts",
            `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(
              2,
              "0"
            )}`
          );
        } catch (error) {
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [isOpen, planId, form]);

  const formSubmit = async (data: SchemaType) => {
    setFormState("loading");
    const [year, month, day] = data?.starts?.split("-");
    const payload = {
      title: data.title,
      starts: {
        day: Number(day),
        month: Number(month),
        year: Number(year),
      },
      planId,
    };

    const { data: apiResponse, ok } = await updateCircuitRoute(payload);
    if (!ok) {
      setFormState("error");

      setTimeout(() => {
        setFormState("default");
      }, 1000);
      setIsOpen(false);
      return;
    }
    updateFront((prev) => ({
      ...prev,
      data: prev?.data?.map((item) =>
        item?.id_circuit_plan === `plans/${planId}`
          ? {
              ...item,
              title: apiResponse?.response?.title,
              starts: `${apiResponse?.response?.starts?.year}-${String(
                apiResponse?.response?.starts?.month
              ).padStart(2, "0")}-${String(apiResponse?.response?.starts?.day).padStart(
                2,
                "0"
              )}`,
            }
          : item
      ),
    }));
    setFormState("default");
    setIsOpen(false);
    toast({
      description: apiResponse?.message,
      action: <Check color="#1edc2b" />,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Rota (Circuit)
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <LoaderSpin />
        ) : (
          <ScrollArea className="h-full">
            <Form
              onSubmit={form.handleSubmit(formSubmit)}
              {...form}
              className="space-y-3"
            >
              <Form.Row>
                <Form.Field name="title">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>
                        Nome <Form.Message />
                      </Form.Label>
                      <Form.Control>
                        <Input {...field} placeholder="Insira um nome" />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>
              <Form.Row>
                <Form.Field name="starts">
                  {({ field }) => (
                    <Form.Item>
                      <Form.Label>Data de Início</Form.Label>
                      <Form.Control>
                        <Input
                          {...field}
                          type="date"
                          placeholder="Selecione uma data"
                        />
                      </Form.Control>
                    </Form.Item>
                  )}
                </Form.Field>
              </Form.Row>

              <DialogFooter>
                <Button
                  type="submit"
                  className={cn("w-full", colors.bg_primary)}
                  disabled={formState !== "default"}
                >
                  {formState === "loading" && (
                    <>
                      Atualizando <LoaderSpin />
                    </>
                  )}
                  {formState === "default" && "Atualizar"}
                  {formState === "error" && "Erro ao atualizar"}
                </Button>
              </DialogFooter>
            </Form>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
